/**
 * DM realtime — the WebSocket layer for the Chat/DM surface.
 *
 * The durable source of truth is the REST layer (services/chat.js, react-query).
 * This socket is a **delivery accelerant only**: it listens on the authorized
 * per-conversation DM socket (messaging/consumers/dm_consumer.py, "DMConsumer")
 * and, on each inbound event, invalidates the relevant react-query caches so the
 * thread and the conversation list re-render with server truth. Sends still go
 * over REST (useSendMessage) — the consumer fans every persisted message back to
 * the whole group (including the sender), so an inbound event after a REST send
 * simply reconciles the optimistic bubble. Nothing here forks business logic.
 *
 * Security / handshake: auth rides a short-lived single-use **ticket** minted at
 * POST /api/v1/dm/ws-ticket/ — the durable token never touches the URL. The
 * ticket endpoint 404s unless BOTH CHAT_ENABLED and CHAT_REALTIME_ENABLED are on
 * server-side, and the consumer close(4503)s while realtime is off, so this hook
 * is INERT-by-default: if realtime isn't live it opens no socket and the app
 * stays exactly REST-only (graceful degradation, zero user-visible change).
 *
 * Dependency: the dm socket is only reachable once messaging/routing.py is wired
 * into asgi.py (the daphne/ASGI ops PR) AND CHAT_REALTIME_ENABLED is set. Until
 * then the first connect fails cleanly and the hook stays REST-only (no retry
 * hammer — see `everOpened` below).
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import API from "./request";
import { API_PATH, QUERY_KEYS, API_BASE_PATH } from "../utils/config";
import { CHAT_ENABLED } from "../constants/featureFlags";
import { getLoggedInUser } from "../utils/helpers";
import {
  dmSocketUrl,
  shouldRefetchOnVisible,
  appendIncomingMessage,
  patchConversationPresence,
  patchConversationDetailPresence,
} from "../pages/Chat/Chat.helpers";

const MAX_RECONNECT = 4;

/** How long a peer's "typing…" indicator stays up after their last `typing`
 *  frame before it auto-clears. Mirrors the standard chat-app convention
 *  (WhatsApp/iMessage ~ a few seconds) — the sender re-sends on a debounce
 *  while actively typing (see Composer.jsx), so this only ever expires once
 *  they've genuinely stopped (or the tab/socket dropped). */
const TYPING_INDICATOR_TIMEOUT_MS = 4000;

/** How often the composer may re-send a `typing` frame while the user keeps
 *  typing — a debounce floor, not a hard interval, so a fast typist doesn't
 *  flood the socket with one frame per keystroke. */
export const TYPING_SEND_THROTTLE_MS = 2000;

/**
 * DM-CM12 — the REST-only degradation this module's banner promises was, until
 * now, a *frozen* thread rather than a slower one: nothing re-read the history
 * while the socket was down, so an open thread showed nothing new until the user
 * manually reloaded the page (MEASURED live on web-stg 2026-07-28: ws-ticket
 * unavailable → 0 recurring history GETs across a 55 s hold with the thread
 * open and focused; the message only appeared after a reload).
 *
 * The conversation LIST already has an honest fallback (MiniDmDock's
 * `useConversations({ refetchInterval: LIVE_BADGE_POLL_MS })`, which keeps
 * running on /messages because the dock's early return happens *after* its
 * hooks). The open thread had none. These two constants are that fallback:
 * a tighter cadence than the badge poll — the user is *looking* at this
 * conversation — and only while the socket is NOT live, so a healthy realtime
 * session costs exactly zero extra requests.
 */
const DEAD_SOCKET_POLL_MS = 15000;

/** A socket that never opened is otherwise never retried for the life of the
 *  mount (`everOpened` below), so a transient 503/network blip demoted the tab
 *  to REST-only until a page reload. Retry a *cold* connect a bounded number of
 *  times, slowly — enough to self-heal, never a hammer while realtime is off. */
const COLD_RETRY_MS = 60000;
const MAX_COLD_RETRY = 3;

/** Mint a single-use WS ticket. Returns the ticket string, or null when the
 *  realtime surface is unavailable (404 while OFF, 503 store-down, any error) —
 *  in which case the caller opens no socket and stays REST-only. Exported so
 *  the /messages route-level prefetch (ISP-9999, MessagesRoutePrefetch) can
 *  fire this request the instant the route matches, ahead of the
 *  MessagesLayout lazy chunk — see fe-dev-canvasjs-defer-and-messages-early-fetch. */
export async function mintTicket() {
  try {
    const res = await API.post(API_PATH.DM_WS_TICKET, {});
    return res?.data?.data?.ticket || null;
  } catch (e) {
    return null; // 404 (realtime off) / 503 (store down) / network → REST-only
  }
}

export function useConversationSocket(conversationId) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const socketRef = useRef(null);
  const typingClearTimerRef = useRef(null);
  const lastTypingSentAtRef = useRef(0);

  useEffect(() => {
    if (!CHAT_ENABLED || !conversationId) {
      setConnected(false);
      setPeerTyping(false);
      return undefined;
    }

    let cancelled = false;
    let socket = null;
    let attempts = 0;
    let coldRetries = 0;
    let everOpened = false;
    let reconnectTimer = null;

    const invalidateThread = () => {
      queryClient.invalidateQueries([QUERY_KEYS.dmMessages, conversationId]);
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    };

    // CHATRT-LAG-0824 — paint the inbound bubble on the FRAME, not on the slow
    // refetch round-trip. The socket frame carries the full server message row
    // (`{type:"message", message:<MessageSerializer>}`), so drop it straight into
    // the thread's react-query cache (id/client_id-deduped). The refetch below
    // still runs to reconcile ordering/pagination + the conversation-list preview,
    // but the bubble no longer WAITS on it.
    const appendMessageToThread = (message) => {
      if (!message) return;
      queryClient.setQueryData(
        [QUERY_KEYS.dmMessages, conversationId],
        (old) => appendIncomingMessage(old, message)
      );
    };

    // A connect that never produced a live socket: stay REST-only (the poll
    // fallback below keeps the thread fresh), but try again a bounded number of
    // times so a transient outage doesn't cost realtime until the next reload.
    const scheduleColdRetry = () => {
      if (cancelled || everOpened) return;
      if (coldRetries >= MAX_COLD_RETRY) return;
      coldRetries += 1;
      reconnectTimer = setTimeout(connect, COLD_RETRY_MS);
    };

    const connect = async () => {
      if (cancelled) return;
      const ticket = await mintTicket();
      if (cancelled) return;
      if (!ticket) {
        scheduleColdRetry(); // realtime unavailable → REST-only, retry later
        return;
      }
      const url = dmSocketUrl(API_BASE_PATH, conversationId, ticket);
      if (!url) return;

      try {
        socket = new WebSocket(url);
      } catch (e) {
        scheduleColdRetry(); // malformed URL / blocked → REST-only
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled) return;
        everOpened = true;
        attempts = 0;
        setConnected(true);
      };

      socket.onmessage = (evt) => {
        if (cancelled) return;
        let data;
        try {
          data = JSON.parse(evt.data);
        } catch (e) {
          return;
        }
        if (data && (data.type === "message" || data.type === "read" || data.type === "reaction")) {
          // CHATRT-LAG-0824 — append the frame's message row FIRST so the bubble
          // paints immediately. `message` and `reaction` frames both carry
          // `data.message` (the reaction toggle re-serializes the message so the
          // new reaction state paints on the frame, same as a new message); a
          // `read` frame doesn't and this is a no-op for it.
          if (data.type === "message" || data.type === "reaction") {
            appendMessageToThread(data.message);
          }
          // Then reconcile: refetch server truth (ordering/pagination) + the
          // conversation-list preview/read-receipts. The paint above no longer
          // blocks on this round-trip.
          invalidateThread();
        } else if (data && data.type === "presence") {
          // Live online/offline dot (messaging/consumers/dm_consumer.py
          // `dm_presence`). A direct cache patch, not invalidateThread — this is
          // a cheap boolean flip, not worth a network round-trip.
          const isOnline = !!data.is_online;
          queryClient.setQueryData([QUERY_KEYS.dmConversations, conversationId], (old) =>
            patchConversationDetailPresence(old, isOnline)
          );
          queryClient.setQueryData([QUERY_KEYS.dmConversations], (old) =>
            patchConversationPresence(old, conversationId, isOnline)
          );
        } else if (data && data.type === "typing") {
          // Ephemeral "peer is typing…" pulse (dm_consumer.py `dm_typing`).
          // Broadcast to the whole group incl. the typer's own socket, so
          // filter out anything that isn't the OTHER participant.
          const myId = getLoggedInUser()?.id;
          if (data.user_id != null && String(data.user_id) !== String(myId)) {
            setPeerTyping(true);
            if (typingClearTimerRef.current) clearTimeout(typingClearTimerRef.current);
            typingClearTimerRef.current = setTimeout(() => {
              setPeerTyping(false);
            }, TYPING_INDICATOR_TIMEOUT_MS);
          }
        }
        // {type:'error'} (forbidden/too_long/…) is a send-side concern surfaced
        // by the REST send path; nothing to merge here.
      };

      socket.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        // Only fast-reconnect a *drop* of a previously-live socket. A socket
        // that never opened (realtime not routed yet, or membership reject)
        // must NOT be retried in a loop — it falls back to the slow, bounded
        // cold ladder while the REST poll below keeps the thread fresh.
        if (!everOpened) {
          scheduleColdRetry();
          return;
        }
        if (attempts < MAX_RECONNECT) {
          const delay = Math.min(1000 * 2 ** attempts, 8000);
          attempts += 1;
          reconnectTimer = setTimeout(connect, delay);
        }
      };

      socket.onerror = () => {
        // onclose follows and owns the reconnect decision.
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        try {
          socket.close();
        } catch (e) {
          /* noop */
        }
      }
      socketRef.current = null;
      setConnected(false);
      if (typingClearTimerRef.current) clearTimeout(typingClearTimerRef.current);
      setPeerTyping(false);
    };
  }, [conversationId, queryClient]);

  /**
   * Fire a `{type:"typing"}` frame at the composer's own debounce floor
   * (`TYPING_SEND_THROTTLE_MS`) — a no-op while the socket isn't live (REST-
   * only degradation applies here too: no socket means no realtime typing
   * signal, exactly like every other frame this hook sends/receives). Callers
   * (Composer.jsx) call this on every keystroke; the throttle here is what
   * keeps that cheap.
   */
  const sendTyping = () => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_SEND_THROTTLE_MS) return;
    lastTypingSentAtRef.current = now;
    try {
      socket.send(JSON.stringify({ type: "typing" }));
    } catch (e) {
      /* noop — a dropped typing pulse is never worth surfacing to the user */
    }
  };

  /*
   * DM-CM12 — REST poll fallback for the OPEN conversation.
   *
   * Runs ONLY while the socket is not live (realtime flag off, ticket 404/503,
   * a connect that never opened, or a drop that exhausted its reconnects), and
   * pauses while the tab is backgrounded — mirroring react-query's own
   * `refetchIntervalInBackground: false` default so a hidden tab costs nothing.
   * Returning to the tab pulls immediately (throttled by the same
   * `shouldRefetchOnVisible` gap the badge poll uses) instead of waiting out the
   * rest of the interval. When the socket IS live this effect does nothing: the
   * event-driven invalidation above stays the only refresh path.
   *
   * Scope: the thread's own history + this conversation's detail row (the read
   * receipt / header). The conversation LIST is deliberately left alone — the
   * always-mounted MiniDmDock already polls it, and duplicating that here would
   * double list traffic for no extra freshness.
   */
  useEffect(() => {
    if (!CHAT_ENABLED || !conversationId || connected) return undefined;

    const messagesKey = [QUERY_KEYS.dmMessages, conversationId];
    const detailKey = [QUERY_KEYS.dmConversations, conversationId];

    const isHidden = () =>
      typeof document !== "undefined" && document.visibilityState === "hidden";

    const refresh = () => {
      if (isHidden()) return;
      queryClient.invalidateQueries(messagesKey);
      queryClient.invalidateQueries(detailKey);
    };

    const timer = setInterval(refresh, DEAD_SOCKET_POLL_MS);

    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      const updatedAt = queryClient.getQueryState(messagesKey)?.dataUpdatedAt;
      if (shouldRefetchOnVisible(updatedAt)) refresh();
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      clearInterval(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [conversationId, connected, queryClient]);

  return { connected, peerTyping, sendTyping };
}

export default useConversationSocket;
